import React, { FC, useEffect, useState } from 'react'
import {
  Button,
  Col,
  Container,
  Form,
  FormControl,
  InputGroup,
  Modal,
  Row,
} from 'react-bootstrap'
import { useTheme } from '@config/theme.config'
import './audioEditor.component.scss'
import { useForm } from 'react-hook-form'
import {
  AudioMessageModel,
  deleteAudioMessage,
  selectAudioMessageById,
} from '@store/slices/audioMessage.slice'
import { useDispatch, useSelector } from 'react-redux'
import { fetchAudioById, selectAllAudios } from '@store/slices/audio.slice'
import { AppDispatch, RootState } from '@store/store'
import {
  createAudioMessageFromFile,
  updateAudioMessageWithFile,
} from '@store/thunk/audioMessage.thunk'
import { toast } from 'react-toastify'
import { EntityId, unwrapResult } from '@reduxjs/toolkit'

type AudioFormData = {
  name: string
  audioStart?: string
  audioEnd?: string
  audioText: string
  audioFile: FileList
  note: string
}

const AudioEditorComponent: FC<{
  audioMessageId: EntityId | undefined
  show: boolean
  onHide: () => void
}> = ({ audioMessageId, show, onHide }) => {
  const { theme } = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const [audioMessageEditId, setAudioMessageEditId] = useState<
    EntityId | undefined
  >(audioMessageId)
  const audioMessageToEdit = useSelector((state: RootState) =>
    audioMessageEditId
      ? selectAudioMessageById(state, audioMessageEditId)
      : null
  )
  const { register, handleSubmit, reset } = useForm<AudioFormData>()
  const audios = useSelector(selectAllAudios)
  const audio = audios.find((audio) => audio.id === audioMessageToEdit?.audioId)

  useEffect(() => {
    if (audioMessageToEdit) dispatch(fetchAudioById(audioMessageToEdit.audioId))
  }, [dispatch, audioMessageToEdit])

  useEffect(() => {
    const values: Partial<AudioFormData> = {
      name: audioMessageToEdit ? audioMessageToEdit.name : '',
      audioStart: audioMessageToEdit ? audioMessageToEdit.beginningWords : '',
      audioEnd: audioMessageToEdit ? audioMessageToEdit.endingWords : '',
      audioText: audioMessageToEdit ? audioMessageToEdit.transcription : '',
      note: audioMessageToEdit ? audioMessageToEdit.note : '',
    }

    reset(values)
  }, [audioMessageToEdit, reset])

  const onSubmitClick = (data: AudioFormData) => {
    submitForm(data, false)
  }
  const onSubmitAndCloseClick = (data: AudioFormData) => {
    submitForm(data, true)
  }

  const submitForm = (data: AudioFormData, close: boolean) => {
    const { name, audioStart, audioEnd, audioText, note, audioFile } = data
    const newAudio: Partial<AudioMessageModel> = {
      ...(audioMessageToEdit ? { id: audioMessageToEdit.id } : {}),
      name,
      ...(note ? { note } : {}),
      ...(audioStart ? { beginningWords: audioStart } : {}),
      ...(audioEnd ? { endingWords: audioEnd } : {}),
      transcription: audioText,
    }
    if (newAudio.id && newAudio.id !== '') {
      dispatch(
        updateAudioMessageWithFile({
          id: newAudio.id,
          data: newAudio,
          file: audioFile[0],
        })
      )
        .then(unwrapResult)
        .then(() => {
          toast.success('Zpráva uložena')
          if (close) onHide()
        })
        .catch((e) => console.error(e))
    } else {
      dispatch(
        createAudioMessageFromFile({
          data: newAudio,
          file: audioFile[0],
        })
      )
        .then(unwrapResult)
        .then((audioMessage) => {
          toast.success('Zpráva vytvořena')
          setAudioMessageEditId(audioMessage.id)
          if (close) onHide()
        })
        .catch((e) => console.error(e))
    }
  }
  const onDeleteClick = () => {
    if (
      audioMessageToEdit &&
      audioMessageToEdit.id &&
      audioMessageToEdit.id !== ''
    )
      dispatch(deleteAudioMessage(audioMessageToEdit.id))
        .then(unwrapResult)
        .then(() => {
          toast.success('Audio smazáno')
          onHide()
        })
        .catch((e) => console.error(e))
  }

  const CloseButtons = () => (
    <div className="d-flex justify-content-between">
      <div className="d-flex gap-1">
        {audioMessageToEdit &&
          audioMessageToEdit.id &&
          audioMessageToEdit.id !== '' && (
            <Button
              variant="danger"
              className="fw-bold"
              onClick={onDeleteClick}
            >
              Smazat
            </Button>
          )}
        <Button variant="warning" className="fw-bold" onClick={() => reset()}>
          Reset
        </Button>
        <Button variant="secondary" className="fw-bold" onClick={onHide}>
          Zavřít
        </Button>
      </div>

      <div className="d-flex justify-content-end gap-1">
        <Button
          variant="primary"
          className="fw-bold"
          onClick={handleSubmit(onSubmitClick)}
        >
          Uložit
        </Button>
        <Button
          variant="primary"
          className="fw-bold"
          onClick={handleSubmit(onSubmitAndCloseClick)}
        >
          Uložit a zavřít
        </Button>
      </div>
    </div>
  )
  const Note = () => (
    <InputGroup>
      <InputGroup.Text>Poznámka</InputGroup.Text>
      <FormControl as="textarea" {...register('note', { required: false })} />
    </InputGroup>
  )
  return (
    <Modal
      data-bs-theme={theme}
      className="audio-editor-modal"
      show={show}
      onHide={onHide}
    >
      <Container className={`text-${theme === 'light' ? theme : 'light'} p-3`}>
        <Form>
          {audioMessageToEdit && audio && (
            <Row className="fw-bold text-secondary px-3 pb-2 d-flex">
              <Col className="d-flex align-items-center">
                <span>ID: {audioMessageToEdit.id}</span>
              </Col>
              <Col>
                <audio controls>
                  <source src={audio.publicPath} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
              </Col>
            </Row>
          )}
          <Row className="mb-2">
            <Col>
              <InputGroup>
                <FormControl
                  placeholder="Vyberte soubor"
                  type="file"
                  {...register('audioFile', { required: !audioMessageToEdit })}
                />
              </InputGroup>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <InputGroup>
                <InputGroup.Text>Název</InputGroup.Text>
                <FormControl
                  type="text"
                  {...register('name', { required: true })}
                />
              </InputGroup>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <InputGroup>
                <InputGroup.Text>Začátek audia</InputGroup.Text>
                <FormControl
                  type="text"
                  placeholder="Tři slova"
                  {...register('audioStart', { required: false })}
                />
              </InputGroup>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <InputGroup>
                <InputGroup.Text>Konec audia</InputGroup.Text>
                <FormControl
                  type="text"
                  placeholder="Tři slova"
                  {...register('audioEnd', { required: false })}
                />
              </InputGroup>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <InputGroup>
                <InputGroup.Text>Přepis audia</InputGroup.Text>
                <FormControl
                  as="textarea"
                  style={{ minHeight: '50px' }}
                  {...register('audioText', { required: false })}
                />
              </InputGroup>
            </Col>
          </Row>
          <Row>
            <Note></Note>
          </Row>
          <Row className="mt-4">
            <CloseButtons></CloseButtons>
          </Row>
        </Form>
      </Container>
    </Modal>
  )
}

export default AudioEditorComponent
